import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ShoppingBag, Mail } from "lucide-react";
import type { LayoutProps } from '../api/types';

export default function Layout({ children, currentPageName }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: "Home", page: "Home" },
    //{ name: "Shop", page: "Shop" },
    //{ name: "Subscriptions", page: "Subscriptions" },
    { name: "Events", page: "Events" },
    //{ name: "Blog", page: "Blog" },
    { name: "About", page: "About" },
    { name: "Kits", page: "Kits" },
    { name: "Gallery (Coming Soon)" }
  ];

  return (
    <div className="min-h-screen bg-[#FDF8F5]">
      <style>{`
        :root {
          --cream: #FDF8F5;
          --dusty-rose: #D4A5A5;
          --sage: #9CAF88;
          --gold: #C9A962;
          --charcoal: #3D3D3D;
        }
        
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Inter:wght@300;400;500;600&display=swap');
        
        .font-serif {
          font-family: 'Cormorant Garamond', serif;
        }
        
        .font-sans {
          font-family: 'Inter', sans-serif;
        }
      `}</style>

      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FDF8F5]/95 backdrop-blur-sm border-b border-[#D4A5A5]/20">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img
                src={process.env.PUBLIC_URL + '/logo.png'}
                alt="Not Junk Journaling Logo"
                className="w-8 h-8 object-contain"
              />
              <span className="font-serif text-2xl font-semibold text-[#3D3D3D]">
                Not Junk Journaling
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navigation.map((item) => (
                <Link
                  key={item.page}
                  to={
                    item.page === "Home" ? "/" :
                    item.page === "Shop" ? "/products" :
                    item.page === "Subscriptions" ? "/subscriptions" :
                    item.page === "Events" ? "/events" :
                    item.page === "Blog" ? "/blog" :
                    item.page === "About" ? "/about" :
                    item.page === "Kits" ? "/kits" : "/"
                  }
                  className={`font-sans text-sm tracking-wide transition-colors duration-200 ${
                    currentPageName === item.page
                      ? "text-[#D4A5A5] font-medium"
                      : "text-[#3D3D3D] hover:text-[#D4A5A5]"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Cart */}
            <div className="flex items-center gap-4">
              {/* <Link to="/cart" className="inline-flex items-center justify-center rounded-md p-2 text-[#3D3D3D] hover:text-[#D4A5A5] hover:bg-[#D4A5A5]/10">
                <ShoppingBag className="w-5 h-5" />
              </Link> */}
              
              {/* Mobile menu button */}
              <button
                className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-[#3D3D3D] hover:text-[#D4A5A5] hover:bg-[#D4A5A5]/10"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle navigation"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#FDF8F5] border-t border-[#D4A5A5]/20">
            <div className="px-4 py-6 space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.page}
                  to={
                    item.page === "Home" ? "/" :
                    item.page === "Shop" ? "/products" :
                    item.page === "Subscriptions" ? "/subscriptions" :
                    item.page === "Events" ? "/events" :
                    item.page === "Blog" ? "/blog" :
                    item.page === "About" ? "/about" :
                    item.page === "Kits" ? "/kits" : "/"
                  }
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block font-sans text-base py-2 ${
                    currentPageName === item.page
                      ? "text-[#D4A5A5] font-medium"
                      : "text-[#3D3D3D]"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#3D3D3D] text-white mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
              <img
                src={process.env.PUBLIC_URL + '/logo.png'}
                alt="Not Junk Journaling Logo"
                className="w-8 h-8 object-contain"
              />
                <span className="font-serif text-xl font-semibold">Not Junk Journaling</span>
              </div>
              <p className="font-sans text-sm text-gray-400 leading-relaxed">
                Creating beautiful spaces for you to explore God's Word through art and journaling.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-serif text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {navigation.map((item) => (
                  <li key={item.page}>
                    <Link
                      to={
                        item.page === "Home" ? "/" :
                        item.page === "Shop" ? "/products" :
                        item.page === "Subscriptions" ? "/subscriptions" :
                        item.page === "Events" ? "/events" :
                        item.page === "Blog" ? "/blog" :
                        item.page === "About" ? "/about" :
                        item.page === "Kits" ? "/kits" : "/"
                      }
                      className="font-sans text-sm text-gray-400 hover:text-[#D4A5A5] transition-colors"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-serif text-lg mb-4">Contact</h4>
              <ul className="space-y-2 font-sans text-sm text-gray-400">
                <li>
                  <a href="mailto:notjunkjournaling@gmail.com" className="hover:text-[#D4A5A5] transition-colors">notjunkjournaling@gmail.com</a>
                </li>
                <li>Follow along on social</li>
              </ul>
              <div className="flex gap-4 mt-4">
                <a href="https://www.facebook.com/profile.php?id=61575678835277" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#D4A5A5] transition-colors" aria-label="Facebook">
                  <svg className="w-5 h-5" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <title>Facebook</title>
                    <path fill="currentColor" d="M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078V12.07h3.047V9.414c0-3.007 1.792-4.668 4.533-4.668 1.313 0 2.686.235 2.686.235v2.953h-1.513c-1.491 0-1.953.925-1.953 1.874v2.263h3.328l-.532 3.472h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="mailto:notjunkjournaling@gmail.com" className="text-gray-400 hover:text-[#D4A5A5] transition-colors" aria-label="Email">
                  <Mail className="w-5 h-5" />
                </a>
                <a href="https://www.youtube.com/@NotJunkJournaling" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#D4A5A5] transition-colors" aria-label="YouTube">
                  <svg className="w-5 h-5" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <title>YouTube</title>
                    <path fill="currentColor" d="M23.498 6.186a2.968 2.968 0 00-2.087-2.087C19.79 3.6 12 3.6 12 3.6s-7.79 0-9.411.499A2.968 2.968 0 00.502 6.186C0 7.808 0 12 0 12s0 4.192.502 5.814a2.968 2.968 0 002.087 2.087C4.21 20.4 12 20.4 12 20.4s7.79 0 9.411-.499a2.968 2.968 0 002.087-2.087C24 16.192 24 12 24 12s0-4.192-.502-5.814zM9.6 15.6V8.4L15.8 12l-6.2 3.6z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Newsletter */}
            {false &&
            <div>
              <h4 className="font-serif text-lg mb-4">Stay Inspired</h4>
              <p className="font-sans text-sm text-gray-400 mb-4">
                Get free journaling pages & updates
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg font-sans text-sm focus:outline-none focus:border-[#D4A5A5]"
                />
                <button className="px-4 py-2 rounded-lg bg-[#D4A5A5] hover:bg-[#C49494] text-white">
                  Join
                </button>
              </div>
            </div>
            }
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 text-center">
            <p className="font-sans text-sm text-gray-500">
              © {new Date().getFullYear()} Not Junk Journaling. Made with love and faith.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}